import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Dropdown,
  makeStyles,
  Option,
  OptionOnSelectData,
  SelectionEvents,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useEffect, useState } from 'react';

import { OptionSwatch } from '@shared/components';
import { TBlockVersionMode } from '../../../documents/types';
import { TSelectVersionDialogState } from '@modules/items/types/dialogs.types';
import { TERMINOLOGY } from '@shared/i18n';

type TProps = {
  state?: TSelectVersionDialogState;
  setState: (state: TSelectVersionDialogState) => void;
};

const useStyles = makeStyles({
  dialogContent: {
    height: '230px',
  },
});

const SelectVersionDialog = ({ state, setState }: TProps) => {
  const [selectedMode, setSelectedMode] = useState<TBlockVersionMode>('AUTO');
  const [selectedVersionId, setSelectedVersionId] = useState<string | undefined>(undefined);

  const handleOptionSelect = (event: SelectionEvents, data: OptionOnSelectData) => {
    setSelectedVersionId(data.selectedOptions[0]);
  };

  const handleSelect = async () => {
    // async to move button to loading state
    await state?.onSelect(selectedMode, selectedMode === 'SPECIFIC' ? selectedVersionId : undefined);
  };

  useEffect(() => {
    if (!state?.isOpen) return;
    setSelectedMode(state.versionMode || 'AUTO');
    setSelectedVersionId(state.versionId);
  }, [state?.isOpen]);

  const disabled = selectedMode === 'SPECIFIC' && !selectedVersionId;

  const optionTexts = state?.optionTexts;
  const classes = useStyles();
  return (
    <Dialog
      open={state?.isOpen || false}
      onOpenChange={(_, data) => {
        if (state) setState({ ...state, isOpen: data.open });
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            {state?.title || 'Select version'}
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <OptionSwatch
              label="Select version:"
              selectedValue={selectedMode}
              setSelectedValue={setSelectedMode as (val: string) => void}
              options={[
                {
                  value: 'AUTO',
                  title: 'Auto',
                  subtitle: optionTexts?.AUTO?.subtitle ?? 'Latest version',
                  tooltip:
                    optionTexts?.AUTO?.tooltip ??
                    `The ${TERMINOLOGY.reference.lower} will always point to the latest version of the file.`,
                },
                {
                  value: 'SPECIFIC',
                  title: 'Specific',
                  subtitle: 'Specific version',
                  tooltip:
                    optionTexts?.SPECIFIC?.tooltip ??
                    `The ${TERMINOLOGY.reference.lower} will point to the specific version you select.`,
                },
              ]}
              className="mb-4"
            />
            {state && selectedMode === 'SPECIFIC' && (
              <Dropdown
                selectedOptions={selectedVersionId ? [selectedVersionId] : []}
                value={!selectedVersionId ? 'None' : selectedVersionId === 'draft' ? 'Draft' : selectedVersionId}
                onOptionSelect={handleOptionSelect}
                appearance="outline"
              >
                {state.versionInfo.draft && <Option value="draft">Draft</Option>}
                {state.versionInfo.versions.map((version) => (
                  <Option value={version.id} key={version.id}>
                    {version.id}
                  </Option>
                ))}
              </Dropdown>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" disabled={disabled} onClick={handleSelect}>
              {state?.buttonLabel || 'Next'}
            </Button>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Cancel</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default SelectVersionDialog;
